import React from "react";
import {
  CheckCircleIcon,
  ExclamationIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";
import { cn } from "./cn";

/**
 * Bun status banner — "state speaks once, firmly": one deep tint, a real
 * 1.5px border in the same family, a dark icon, a semibold first line.
 * Never stack a banner with badges or side-bars carrying the same message.
 */
const VARIANTS = {
  info: {
    box: "bg-bun-info-bg border-bun-info-border",
    text: "text-bun-info",
    Icon: InformationCircleIcon,
  },
  success: {
    box: "bg-bun-success-bg border-bun-success-border",
    text: "text-bun-success",
    Icon: CheckCircleIcon,
  },
  warning: {
    box: "bg-bun-warning-strong-bg border-bun-warning-border",
    text: "text-bun-warning-strong",
    Icon: ExclamationIcon,
  },
  danger: {
    box: "bg-bun-danger-bg border-bun-danger-border",
    text: "text-bun-danger-strong",
    Icon: ExclamationCircleIcon,
  },
};

export default function Banner({ variant = "info", icon, title, action, className, children, ...props }) {
  const { box, text, Icon: DefaultIcon } = VARIANTS[variant] || VARIANTS.info;
  // `icon` swaps the glyph while keeping the variant's tint (the canvas puts
  // a mail icon on the reset-link-sent banner, for example).
  const Icon = icon || DefaultIcon;
  return (
    <div
      role={variant === "danger" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-[10px] rounded-bun border-[1.5px] px-[13px] py-[11px] font-instrument",
        box,
        className
      )}
      {...props}
    >
      <Icon aria-hidden="true" className={cn("w-4 h-4 mt-[1px] shrink-0", text)} />
      <div className={cn("flex-1 min-w-0 text-[12.5px] leading-relaxed font-book", text)}>
        {title && <div className="font-semibold">{title}</div>}
        {children}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
